import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Card, Loader } from "@/components/ui-helpers";
import { SendLinkButtons } from "@/components/SendLinkButtons";
import { ClipboardList } from "lucide-react";

const fmt = (iso) => (iso ? new Date(iso).toLocaleString("en-CA", { dateStyle: "medium", timeStyle: "short" }) : "");

export function CaseQuestionnaire({ appId, canEdit }) {
  const [qn, setQn] = useState(undefined);
  const load = () => api.get(`/applications/${appId}/questionnaire`).then((r) => setQn(r.data.questionnaire)).catch(() => setQn(null));
  useEffect(() => { load(); }, [appId]); // eslint-disable-line

  const send = (channel) => api.post(`/applications/${appId}/questionnaire/send`, { channel }).then((r) => r.data);

  if (qn === undefined) return <Loader />;
  const submitted = qn?.status === "Submitted";
  return (
    <Card className="p-6 space-y-4" data-testid="case-questionnaire">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h3 className="font-head font-bold text-slate-900 flex items-center gap-2"><ClipboardList className="h-5 w-5 text-primary" /> Questionnaire</h3>
          {qn ? (
            <p className="text-xs text-slate-500 mt-1" data-testid="questionnaire-status">
              {qn.set_name} set · <b>{qn.status}</b>
              {qn.emailed_to && ` · emailed to ${qn.emailed_to} ${fmt(qn.sent_at)}`}
              {submitted && ` · submitted ${fmt(qn.submitted_at)}`}
            </p>
          ) : <p className="text-xs text-slate-500 mt-1">No questionnaire has been sent for this case yet.</p>}
          {qn?.last_delivery?.error && !submitted && <p data-testid="questionnaire-delivery-error" className="mt-2 text-xs rounded-lg bg-amber-50 border border-amber-200 text-amber-800 px-2.5 py-1.5">Not emailed: {qn.last_delivery.error}</p>}
        </div>
        <div className="flex flex-col items-end gap-1">
          <span className="text-[11px] text-slate-400">{qn ? (submitted ? "Send a new one" : "Resend") : "Send questionnaire"}</span>
          <SendLinkButtons testid="questionnaire-send" send={send} onDone={(r) => r?.questionnaire && setQn(r.questionnaire)} />
        </div>
      </div>
      {submitted ? (
        <dl className="divide-y divide-slate-100" data-testid="questionnaire-answers">
          {(qn.answers || []).map((a) => (
            <div key={a.id} className="py-2.5 grid sm:grid-cols-[2fr_3fr] gap-1 sm:gap-4 text-sm">
              <dt className="text-slate-500">{a.label}</dt>
              <dd className="text-slate-900 whitespace-pre-wrap">{a.answer || <span className="text-slate-300">—</span>}</dd>
            </div>
          ))}
        </dl>
      ) : qn && (
        <ol className="list-decimal pl-5 space-y-1 text-sm text-slate-600" data-testid="questionnaire-questions">
          {(qn.questions || []).map((q) => <li key={q.id}>{q.label}</li>)}
        </ol>
      )}
    </Card>
  );
}
